import { appConfig } from '@/config';

const TRANSLATION_ENDPOINT = '/business-api/message-translate.php';

export function nlcTranslationScopeKey(conversationId, language, token) {
  return JSON.stringify([String(conversationId || ''), String(language || ''), String(token || '')]);
}

export function nlcIndicatorState(payload = {}) {
  const nlc = payload.nlc || {};
  if (['stopped', 'banned'].includes(nlc.admin_status)) {
    return 'exhausted';
  }
  if (!nlc.enabled) {
    return 'off';
  }
  if (!payload.service_enabled || !nlc.has_access) {
    return 'exhausted';
  }
  if (['professional', 'enterprise'].includes(nlc.plan)) {
    return 'active';
  }

  const remainingMinutes = Number(nlc.remaining_minutes);
  if (Number.isFinite(remainingMinutes)) {
    if (remainingMinutes <= 0) {
      return 'exhausted';
    }
    if (remainingMinutes <= 60) {
      return 'warning';
    }
  }
  return 'active';
}

export class MessageTranslationError extends Error {
  constructor(message, code = '') {
    super(message);
    this.name = 'MessageTranslationError';
    this.code = code;
  }
}

async function nlcRequest(body, signal) {
  const response = await fetch(new URL(TRANSLATION_ENDPOINT, appConfig.serviceOrigin), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
    signal
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new MessageTranslationError(
      payload.message || `NLC 请求失败 (${response.status})`,
      payload.errorCode || ''
    );
  }
  return payload;
}

export async function getNlcStatus({ token, signal } = {}) {
  return nlcRequest({ action: 'status', token }, signal);
}

export async function setNlcEnabled({ token, enabled, signal } = {}) {
  return nlcRequest({ action: 'preference', token, enabled: Boolean(enabled) }, signal);
}

export async function getNlcSupportStaff({ token, signal } = {}) {
  const payload = await nlcRequest({ action: 'support_staff', token }, signal);
  return Array.isArray(payload.staff) ? payload.staff : [];
}

export async function translateMessage({ token, targetLanguage, message, context = [], cachedOnly = false, signal }) {
  const payload = await nlcRequest({ action: 'translate', token, target_language: targetLanguage, message, context, cached_only: Boolean(cachedOnly) }, signal);
  return payload.translation || null;
}

export async function translateVoiceMessage({ token, targetLanguage, messageId, sourceLanguage = '', audio, filename = 'voice.webm', context = [], cachedOnly = false, signal }) {
  const form = new FormData();
  form.set('action', 'voice_translate');
  form.set('token', token);
  form.set('target_language', targetLanguage);
  form.set('message_id', messageId);
  form.set('source_language', sourceLanguage);
  form.set('context', JSON.stringify(context));
  form.set('cached_only', String(Boolean(cachedOnly)));
  form.set('audio', audio, filename);
  const response = await fetch(new URL(TRANSLATION_ENDPOINT, appConfig.serviceOrigin), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
    signal
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new MessageTranslationError(
      payload.message || `NLC 语音翻译失败 (${response.status})`,
      payload.errorCode || ''
    );
  }
  return payload.translation || null;
}

export async function transcribeVoiceMessage({ token, sourceLanguage = '', audio, filename = 'dictation.webm', signal }) {
  const form = new FormData();
  form.set('action', 'transcribe');
  form.set('token', token);
  form.set('source_language', sourceLanguage);
  form.set('audio', audio, filename);
  const response = await fetch(new URL(TRANSLATION_ENDPOINT, appConfig.serviceOrigin), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
    signal
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new MessageTranslationError(
      payload.message || `语音识别失败 (${response.status})`,
      payload.errorCode || ''
    );
  }
  return {
    text: String(payload.text || ''),
    language: String(payload.language || '')
  };
}
